import { useState, useRef, useEffect, useCallback } from 'react';
import type React from 'react';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { useApiGetCohorts } from '../../hooks/use-api-get-cohorts';

import Icons from 'assets/icons';

interface CohortLike {
  id?: string | number;
  name?: string;
}

interface CohortsListProps {
  cohorts?: CohortLike[];
  isLoading: boolean;
  isError: boolean;
}

const CohortsList = ({
  cohorts,
  isLoading,
  isError,
}: CohortsListProps): React.JSX.Element | React.JSX.Element[] => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <Icons.Spinner className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600 leading-5">Failed to load cohorts</p>
    );
  }

  if (!cohorts?.length) {
    return (
      <p className="text-sm text-gray-500 leading-5">No cohorts available</p>
    );
  }

  return cohorts.map((cohort) => (
    <p
      key={cohort.id ?? cohort.name}
      className="text-sm leading-5 text-gray-700 truncate"
    >
      {cohort.name}
    </p>
  ));
};

interface CohortsPopoverProps {
  clientId: string | number;
}

const CohortsPopover = ({
  clientId,
}: CohortsPopoverProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const currentUser = useCurrentUser();

  const {
    data: cohorts,
    isLoading,
    isError,
  } = useApiGetCohorts({
    oktaUserId: currentUser?.data?.oktaUserId as string,
    clientId,
    enabled: isOpen,
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node | null;

      if (
        target &&
        !popoverRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        handleClose();
      }
    };

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  return (
    <div className="relative">
      <Button
        ref={buttonRef as never}
        variant="reserve-invert"
        size="xs"
        className="shadow"
        onClick={() => setIsOpen((prev) => !prev)}
        data-testid="cohorts-button"
      >
        Cohorts
      </Button>

      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute right-0 top-full mt-1 w-[200px] bg-white border border-navy-100 rounded-md shadow-md z-50 py-4 px-5"
          role="dialog"
          aria-label="Cohorts"
        >
          <IconButton
            variant="neutral-text"
            size="baby"
            className="!absolute right-2 top-2"
            icon={<Icons.Close />}
            onClick={handleClose}
            aria-label="Close cohorts"
            data-testid="cohorts-close-button"
          />
          <h3 className="text-base font-semibold leading-6 text-navy-800 mb-2">
            Cohorts
          </h3>

          <div className="flex flex-col gap-2">
            <CohortsList
              cohorts={cohorts as CohortLike[] | undefined}
              isLoading={isLoading}
              isError={isError}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CohortsPopover;
